import { Box, Stack, useTheme } from "@mui/material";
import { DataTable, DownloadIcon, Drawer, FileIcon } from "components";
import { useGetAllInvoice } from "controller";
import { memo, useCallback, useMemo } from "react";
import { dayjs } from "utilz";

const InvoiceHeaders = [
  {
    name: "Invoice",
    sortable: false,
    key: "id",
  },
  {
    name: "Billing Date",
    sortable: false,
    key: "billingDate",
  },
  {
    name: "Amount",
    sortable: false,
    key: "amount",
  },
  {
    name: "",
    sortable: false,
    key: "actions",
  },
];

const cellBuilders = {
  id: (rowData: Record<string, unknown>) => {
    return (
      <Stack direction="row" gap="8px" alignItems="center">
        <FileIcon type="application/pdf" />
        Invoice #{rowData?.number as string}
      </Stack>
    );
  },
  billingDate: (rowData: Record<string, unknown>) => {
    return <span>{dayjs.unix(rowData?.created as number).format("LL")}</span>;
  },
  amount: (rowData: Record<string, unknown>) => {
    return <span>USD ${(rowData?.amount_paid as number) / 100}</span>;
  },
  actions: () => {
    return (
      <span>
        <DownloadIcon aria-label="Download invoice" />
      </span>
    );
  },
};

interface InvoicesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const InvoicesDrawer = memo(({ open, onClose }: InvoicesDrawerProps) => {
  const theme = useTheme();
  const { data: invoices, isLoading } = useGetAllInvoice();

  const filteredData = useMemo(() => {
    return (
      invoices?.data.filter(
        (invoice: Record<string, unknown>) =>
          invoice?.status === "paid" || invoice?.status === "open",
      ) || []
    );
  }, [invoices?.data]);

  const handleRowClick = useCallback((rowData: Record<string, unknown>) => {
    window.open(
      rowData?.invoice_pdf as string,
      "_blank",
      "noopener,noreferrer",
    );
  }, []);

  if (!open) return null;

  return (
    <Drawer open={open} title="Invoices" onClose={onClose} footer={<></>}>
      <Box
        sx={{
          width: "800px",
          p: "8px",
          backgroundColor: theme.palette.neutral["005"],
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFF",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <DataTable
            isDataLoading={isLoading}
            cellBuilders={cellBuilders}
            headers={InvoiceHeaders}
            data={filteredData}
            selectedItems={[]}
            currentPageNumber={0}
            multiselect={false}
            itemsPerPage={0}
            handleRowClick={handleRowClick}
          />
        </Box>
      </Box>
    </Drawer>
  );
});

InvoicesDrawer.displayName = "InvoicesDrawer";
